import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { GamepadIcon, MapPin, Calendar, DollarSign, Plus, TrendingUp, TrendingDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface Game {
  id: string;
  title?: string;
  location?: string;
  description?: string;
  buyin_amount: number;
  chips_per_buyin: number;
  status: string;
  created_at: string;
}

interface Player {
  id: string;
  name: string;
}

interface GameListProps {
  games: Game[];
  players: Player[];
  groupId: string;
  onUpdate: () => void;
  currentUserId?: string | null;
  onCreateGame?: () => void;
}

export const GameList = ({ games, players, currentUserId, onCreateGame }: GameListProps) => {
  const navigate = useNavigate();
  const [userPayouts, setUserPayouts] = useState<Record<string, number>>({});

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString()}`;
  };

  // Fetch user payouts for settled games
  useEffect(() => {
    const fetchUserPayouts = async () => {
      if (!currentUserId) return;
      
      const settledGameIds = games.filter(g => g.status === 'settled').map(g => g.id);
      if (settledGameIds.length === 0) return;

      try {
        // First find the player record for the current user
        const { data: playerData } = await supabase
          .from('players')
          .select('id')
          .eq('auth_user_id', currentUserId)
          .single();

        if (!playerData) return;

        // Then fetch payouts using the player's ID
        const { data: payouts } = await supabase
          .from('payouts')
          .select('game_id, net_amount')
          .in('game_id', settledGameIds)
          .eq('player_id', playerData.id);

        if (payouts) {
          const payoutMap = payouts.reduce((acc, payout) => {
            acc[payout.game_id] = payout.net_amount;
            return acc;
          }, {} as Record<string, number>);
          setUserPayouts(payoutMap);
        }
      } catch (error) {
        console.error('Error fetching user payouts:', error);
      }
    };

    fetchUserPayouts();
  }, [games, currentUserId]);

  const getUserPayoutDisplay = (game: Game) => {
    if (game.status !== 'settled' || !currentUserId) {
      return (
        <div className="flex items-center text-sm text-muted-foreground">
          <GamepadIcon className="h-3 w-3 mr-1" />
          {game.status === 'in_progress' ? 'In Progress' : 'No Data'}
        </div>
      );
    }

    const payout = userPayouts[game.id];
    if (payout === undefined) {
      return (
        <div className="flex items-center text-sm text-muted-foreground">
          <GamepadIcon className="h-3 w-3 mr-1" />
          Not Played
        </div>
      );
    }

    const isProfit = payout > 0;
    return (
      <div className={`flex items-center text-sm font-medium ${
        isProfit ? 'text-green-600' : payout < 0 ? 'text-red-600' : 'text-muted-foreground'
      }`}>
        {isProfit ? (
          <TrendingUp className="h-3 w-3 mr-1" />
        ) : payout < 0 ? (
          <TrendingDown className="h-3 w-3 mr-1" />
        ) : (
          <GamepadIcon className="h-3 w-3 mr-1" />
        )}
        {payout === 0 ? 'Break Even' : `${isProfit ? '+' : ''}${formatCurrency(payout)}`}
      </div>
    );
  };

  if (games.length === 0) {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <GamepadIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No games yet</h3>
          <p className="text-muted-foreground mb-4">
            {players.length === 0 
              ? "Add players first, then create your first game"
              : "Create your first poker game to get started"
            }
          </p>
          {players.length > 0 && (
            <Button onClick={onCreateGame}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Game
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Games ({games.length})</h3>
        <div className="flex items-center space-x-2">
          <Badge variant="secondary">
            {games.filter(g => g.status === 'in_progress').length} Active
          </Badge>
          <Badge variant="outline">
            {games.filter(g => g.status === 'settled').length} Completed
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {games.map((game) => (
          <Card 
            key={game.id} 
            className="hover:shadow-md transition-all duration-200 cursor-pointer"
            onClick={() => navigate(`/game/${game.id}`)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  {game.title || "Untitled Game"}
                </CardTitle>
                <Badge 
                  variant={game.status === 'in_progress' ? 'default' : 'secondary'}
                >
                  {game.status === 'in_progress' ? 'Active' : 'Settled'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center text-muted-foreground">
                  <DollarSign className="h-3 w-3 mr-1" />
                  Buy-in: {formatCurrency(game.buyin_amount)}
                </div>
                <div className="flex items-center text-muted-foreground">
                  <GamepadIcon className="h-3 w-3 mr-1" />
                  Chips: {game.chips_per_buyin.toLocaleString()}
                </div>
              </div>
              
              {/* User Win/Loss Display */}
              <div className="pt-2 border-t border-border">
                {getUserPayoutDisplay(game)}
              </div>
              
              {game.location && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <MapPin className="h-3 w-3 mr-1" />
                  {game.location}
                </div>
              )}
              
              <div className="flex items-center text-sm text-muted-foreground">
                <Calendar className="h-3 w-3 mr-1" />
                {formatDate(game.created_at)}
              </div>

              <Button 
                className="w-full mt-3" 
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/game/${game.id}`);
                }}
              >
                {game.status === 'in_progress' ? 'Manage Game' : 'View Details'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};